import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { Inventory2, Warehouse, FolderOpen, ArrowBack, Folder } from '@mui/icons-material';
import PageHeader from '../../../components/PageHeader';
import warehouseService from '../services/warehouseService';
import { extractErrorMessage } from '../../../utils/errorUtils';

const BoxDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [box, setBox] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    warehouseService
      .getBoxById(id)
      .then((res) => {
        if (res?.data) {
          setBox(res.data);
        } else {
          setError('Caja no encontrada');
        }
      })
      .catch((err) => setError(extractErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !box) {
    return (
      <Box p={3}>
        <Alert severity="error">{error || 'Caja no encontrada'}</Alert>
        <Button sx={{ mt: 2 }} startIcon={<ArrowBack />} onClick={() => navigate('/warehouses')}>
          Volver
        </Button>
      </Box>
    );
  }

  const warehouse = box.box_warehouse?.[0]?.warehouses || null;
  const bw = box.box_warehouse?.[0];
  const proceedings = box.boxProceedings || [];

  return (
    <Box>
      <PageHeader
        title={`Caja: ${box.code}`}
        subtitle={box.company?.name || ''}
        actions={
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/warehouses')}>
            Volver
          </Button>
        }
      />

      {/* Fila 1: Info de caja y bodega */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {/* Caja */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Inventory2 color="primary" fontSize="small" />
              <Typography variant="subtitle1" fontWeight={700}>Información de la Caja</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">Código</Typography>
            <Typography variant="body1" fontWeight={600} mb={1.5}>{box.code}</Typography>
            <Typography variant="body2" color="text.secondary">Empresa</Typography>
            <Typography variant="body1">{box.company?.name || '—'}</Typography>
          </Box>

          <Divider orientation="vertical" flexItem />

          {/* Bodega */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Warehouse color="primary" fontSize="small" />
              <Typography variant="subtitle1" fontWeight={700}>Bodega</Typography>
            </Box>
            {warehouse ? (
              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Nombre</Typography>
                  <Typography variant="body1" fontWeight={600}>{warehouse.name}</Typography>
                </Box>
                {warehouse.code && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Código</Typography>
                    <Typography variant="body1">{warehouse.code}</Typography>
                  </Box>
                )}
                {bw?.island && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Isla</Typography>
                    <Typography variant="body1">{bw.island}</Typography>
                  </Box>
                )}
                {bw?.shelving && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Estante</Typography>
                    <Typography variant="body1">{bw.shelving}</Typography>
                  </Box>
                )}
                {bw?.shelf && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Nivel</Typography>
                    <Typography variant="body1">{bw.shelf}</Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">Sin bodega asignada</Typography>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Fila 2: Tabla de expedientes */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <FolderOpen color="primary" />
        <Typography variant="h6">Expedientes</Typography>
        <Chip label={proceedings.length} size="small" color="primary" />
      </Box>

      {proceedings.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Folder sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography>No hay expedientes asociados a esta caja</Typography>
        </Paper>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Código</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {proceedings.map((bp) => (
                  <TableRow
                    key={bp.id}
                    hover
                    onClick={() => navigate(`/proceedings/${bp.proceeding.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Folder color="primary" />
                        <strong>{bp.proceeding.name}</strong>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={bp.proceeding.code} size="small" color="primary" variant="outlined" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default BoxDetail;
